import { fetchAuthSession } from 'aws-amplify/auth'

import fetcher, { formDataFetcher } from '../fetcher'
import config from '../../config'
import { EducationLevel, UserPreferences, type User, type UserStudies, type StudentLanguage, type StudentLanguageDto, type UserLanguagesResponse, type UserCv, type UserAcademicTranscript } from './types'
import { IResponseBody } from '../types'

export const getCurrentUser = async (): Promise<User | undefined> => {
	try {
		const res: IResponseBody<User> = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/users`, { method: 'GET' })
		return res.data
	} catch (error: unknown) {
		console.error('Error fetching current user', error)
		throw error
	}
}

export const patchOnboardingUser = async (_: string, { arg }: { arg: { body: Partial<User> } }): Promise<User | undefined> => {
	const res:IResponseBody<User> = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/users`, { method: 'PATCH', body: JSON.stringify(arg.body) })
	await fetchAuthSession({ forceRefresh: true })
	return res.data
}

export const softDeleteUser = async (): Promise<void> => {
	try {
		await fetcher(`${config.VITE_ZRIVE_API_URL}/api/users`, { method: 'DELETE' })
	} catch (error: unknown) {
		console.error('Error deleting user', error)
		throw error
	}
}

export const updateUserStudies = async (_: string, { arg }: { arg: { body: Partial<UserStudies> } }): Promise<UserStudies> => {
	try {
		const formattedBody: Partial<UserStudies> = {
			educationLevel: arg.body.educationLevel,
			bachelorUniversityId: arg.body.bachelorUniversityId,
			bachelorId: arg.body.bachelorId,
		}
		if(arg.body.educationLevel === EducationLevel.STUDYING_BACHELOR) {
			formattedBody.bachelorStartYear = arg.body.bachelorStartYear
			formattedBody.doneProfessionalInternships = arg.body.doneProfessionalInternships
			formattedBody.bachelorStartYear = arg.body.bachelorStartYear
			if (formattedBody.doneProfessionalInternships?.toString() === 'true') {
				formattedBody.internshipIndustryId = arg.body.internshipIndustryId
			}
		} else if(arg.body.educationLevel === EducationLevel.STUDYING_MASTER) {
			formattedBody.masterName = arg.body.masterName
			formattedBody.masterUniversityId = arg.body.masterUniversityId
			formattedBody.masterEndYear = arg.body.masterEndYear
			formattedBody.doneProfessionalInternships = arg.body.doneProfessionalInternships
			if (formattedBody.doneProfessionalInternships?.toString() === 'true') {
				formattedBody.internshipIndustryId = arg.body.internshipIndustryId
			}
		} else if (arg.body.educationLevel === EducationLevel.GRADUATED) {
			formattedBody.isWorking = arg.body.isWorking
			if (formattedBody.isWorking?.toString() === 'true') {
				formattedBody.workingIndustryId = arg.body.workingIndustryId
			}
			formattedBody.qualificationLevel = arg.body.qualificationLevel
		}
		const res = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/users/studies`, { method: 'PATCH', body: JSON.stringify(formattedBody) })
		return res.data
	} catch (error: unknown) {
		console.error('Error updating user studies', error)
		throw error
	}
}

export const getUserStudies = async (): Promise<Partial<UserStudies> | undefined> => {
	try {
		const res = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/users/studies`, { method: 'GET' })
		return res.data
	} catch (error: unknown) {
		console.error('Error fetching user studies', error)
		throw error
	}
}

export const updateUserPreferences = async (_: string, { arg }: { arg: { body: Partial<UserPreferences> } }): Promise<UserPreferences> => {
	try {
		const formattedBody: Partial<UserPreferences> = {
			knowingProffesionalOportunities: arg.body.knowingProffesionalOportunities,
			knowingCareerGoal: arg.body.knowingCareerGoal,
			goals: arg.body.goals,
			favouriteCategories: arg.body.favouriteCategories,
		}
		const res = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/users/preferences`, { method: 'PATCH', body: JSON.stringify(formattedBody) })
		return res.data
	} catch (error: unknown) {
		console.error('Error updating user preferences', error)
		throw error
	}
}

export const updateUserNewsPreferences = async (_: string, { arg }: { arg: { body: Partial<UserPreferences> } }): Promise<UserPreferences> => {
	try {
		const formattedBody: Partial<UserPreferences> = {
			phone: arg.body.phone ?? '',
			phoneCountryCode: arg.body.phoneCountryCode,
			optinWhatsapp: arg.body.optinWhatsapp ?? false,
			optinJobOffers: arg.body.optinJobOffers ?? false,
			favouriteCareerPaths: arg.body.favouriteCareerPaths,
		}
		const res = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/users/preferences`, { method: 'PATCH', body: JSON.stringify(formattedBody) })
		return res.data
	} catch (error: unknown) {
		console.error('Error updating user news preferences', error)
		throw error
	}
}

export const getUserPreferences = async (): Promise<Partial<UserPreferences> | undefined> => {
	try {
		const res = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/users/preferences`, { method: 'GET' })
		const formattedData = res.data
			? {
				...res.data,
				favouriteCategories: res.data.favouriteCategories?.map((cat: { id: number }) => cat.id),
			}
			: res.data
		return formattedData
	} catch (error: unknown) {
		console.error('Error fetching user preferences', error)
		throw error
	}
}

export const updateOnboardingCompleted = async (): Promise<boolean> => {
	try {
		const res = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/users/onboarding-completed`, { method: 'PATCH' })
		return res.data
	} catch (error: unknown) {
		console.error('Error updating onboarding completed', error)
		throw error
	}
}

const LANGUAGES_BASE = `${config.VITE_ZRIVE_API_URL}/api/users/languages`

export const getUserLanguages = async (): Promise<UserLanguagesResponse> => {
	const res: IResponseBody<UserLanguagesResponse> = await fetcher(LANGUAGES_BASE, { method: 'GET' })
	return res.data ?? { items: [], languages: [], levels: [] }
}

export const createUserLanguage = async (_: string, { arg }: { arg: StudentLanguageDto }): Promise<StudentLanguage> => {
	const res: IResponseBody<StudentLanguage> = await fetcher(LANGUAGES_BASE, {
		method: 'POST',
		body: JSON.stringify(arg)
	})
	return res.data as StudentLanguage
}

export const updateUserLanguage = async (_: string, { arg }: { arg: { code: string, level: number } }): Promise<StudentLanguage> => {
	const res: IResponseBody<StudentLanguage> = await fetcher(`${LANGUAGES_BASE}/${encodeURIComponent(arg.code)}`, {
		method: 'PATCH',
		body: JSON.stringify({ level: arg.level })
	})
	return res.data as StudentLanguage
}

export const deleteUserLanguage = async (_: string, { arg }: { arg: string }): Promise<void> => {
	await fetcher(`${LANGUAGES_BASE}/${encodeURIComponent(arg)}`, { method: 'DELETE' })
}

export interface SendZriveReferralBody {
	referredEmail: string
	referredName?: string
	zriveProgramId: number
}

export const sendZriveReferral = async (body: SendZriveReferralBody): Promise<unknown> => {
	const res = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/users/send-zrive-referral`, {
		method: 'POST',
		body: JSON.stringify(body),
	})
	return res.data
}

export interface ZriveReferralSignupListItem {
	id: number
	zriveProgram: { id: number; name: string; url?: string | null; enableForReferralProgram?: boolean }
	referredUser: { id: number; email: string; name: string; lastName: string } | null
	referredName: string
	referredEmail: string
	referredJoinedZrive: boolean
}

export const getMyZriveReferralSignups = async (): Promise<ZriveReferralSignupListItem[]> => {
	const res = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/users/zrive-referral-signup`, { method: 'GET' })
	return res.data ?? []
}

export interface ZriveReferralRewardListItem {
	id: number
	userId: number
	rewardId: number
	claimed: boolean
	claimedAt: string | null
	referralCount: number
	readyToClaim: boolean
	createdAt: string
	updatedAt: string
	reward: {
		id: number
		name: string
		description: string
		referralGoalAmount: number
	}
}

export const getMyZriveReferralRewards = async (): Promise<ZriveReferralRewardListItem[]> => {
	const res = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/users/zrive-referral-signup/rewards`, { method: 'GET' })
	return res.data ?? []
}

export const claimZriveReferralReward = async (rewardUserId: number): Promise<void> => {
	await fetcher(`${config.VITE_ZRIVE_API_URL}/api/users/zrive-referral-signup/rewards/${rewardUserId}/claim`, {
		method: 'PATCH',
	})
}

export const getUserCv = async (): Promise<UserCv | null> => {
	try {
		const res: IResponseBody<UserCv> = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/users/cv`, { method: 'GET' })
		return res.data ?? null
	} catch (error: unknown) {
		console.error('Error fetching user CV', error)
		throw error
	}
}

export const uploadUserCV = async (_: string, { arg }: { arg: { file: File } }): Promise<UserCv> => {
	try {
		const formData = new FormData()
		formData.append('cv', arg.file)
		const res: IResponseBody<UserCv> = await formDataFetcher(`${config.VITE_ZRIVE_API_URL}/api/users/cv`, formData, 'POST')
		if (!res?.data) {
			throw new Error('User CV was not created')
		}
		return res.data
	} catch (error: unknown) {
		console.error('Error creating user CV', error)
		throw error
	}
}

export const getUserAcademicTranscript = async (): Promise<UserAcademicTranscript | null> => {
	try {
		const res: IResponseBody<UserAcademicTranscript> = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/users/academic-transcript`, { method: 'GET' })
		return res.data ?? null
	} catch (error: unknown) {
		console.error('Error fetching user academic transcript', error)
		throw error
	}
}

export const uploadUserAcademicTranscript = async (_: string, { arg }: { arg: { file: File } }): Promise<UserAcademicTranscript> => {
	try {
		const formData = new FormData()
		formData.append('academicTranscript', arg.file)
		const res: IResponseBody<UserAcademicTranscript> = await formDataFetcher(`${config.VITE_ZRIVE_API_URL}/api/users/academic-transcript`, formData, 'POST')
		if (!res?.data) {
			throw new Error('User academic transcript was not created')
		}
		return res.data
	} catch (error: unknown) {
		console.error('Error creating user academic transcript', error)
		throw error
	}
}
